import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Lock, Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';

const ResetPassword = () => {
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validToken, setValidToken] = useState(null); // null = verificando, true = válido, false = inválido

  // Verificar se há uma sessão de recuperação válida
  useEffect(() => {
    const checkSession = async () => {
      // O Supabase automaticamente processa o token na URL e cria uma sessão temporária
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        console.log('Nenhuma sessão de recuperação encontrada');
        setValidToken(false);
        return;
      }

      // Verifica se é uma sessão de recovery
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      
      if (type === 'recovery' || session) {
        setValidToken(true);
      } else {
        setValidToken(false);
      }
    };

    checkSession();

    // Listener para mudanças de auth (quando o token é processado)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setValidToken(true);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validações
    if (!password || !confirmPassword) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ 
        password: password 
      });

      if (error) {
        throw error;
      }

      setSuccess(true);
      
      // Fazer logout após redefinir a senha para forçar novo login
      await supabase.auth.signOut();
      
      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      console.error('Erro ao redefinir senha:', err);
      setError(err.message || 'Erro ao redefinir senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Tela de carregamento enquanto verifica token
  if (validToken === null) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: '#FDFBF7' }}
      >
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#4A6B5D' }} />
          <p style={{ color: '#5C6B64' }}>Verificando link de recuperação...</p>
        </div>
      </div>
    );
  }

  // Tela de link inválido/expirado
  if (validToken === false) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: '#FDFBF7' }}
      >
        <Card 
          className="w-full max-w-md border shadow-sm"
          style={{ 
            backgroundColor: '#FFFFFF', 
            borderColor: '#EAE6DF',
            borderRadius: '1rem'
          }}
        >
          <CardContent className="pt-8 pb-8 text-center">
            <div 
              className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: '#FEF2F2' }}
            >
              <AlertTriangle className="h-8 w-8" style={{ color: '#B75D69' }} />
            </div>
            <h2 
              className="text-xl font-semibold mb-2"
              style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}
            >
              Link Inválido ou Expirado
            </h2>
            <p className="text-sm mb-6" style={{ color: '#5C6B64' }}>
              O link de recuperação de senha é inválido ou já expirou. 
              Por favor, solicite um novo link.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
              style={{ backgroundColor: '#4A6B5D', borderRadius: '0.75rem' }}
            >
              Voltar ao Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de sucesso
  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: '#FDFBF7' }}
      >
        <Card 
          className="w-full max-w-md border shadow-sm"
          style={{ 
            backgroundColor: '#FFFFFF', 
            borderColor: '#EAE6DF',
            borderRadius: '1rem'
          }}
        >
          <CardContent className="pt-8 pb-8 text-center">
            <div 
              className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: '#E8F5E9' }}
            >
              <CheckCircle className="h-8 w-8" style={{ color: '#4A6B5D' }} />
            </div>
            <h2 
              className="text-xl font-semibold mb-2"
              style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}
            >
              Senha Redefinida!
            </h2>
            <p className="text-sm mb-2" style={{ color: '#5C6B64' }}>
              Sua senha foi alterada com sucesso.
            </p>
            <p className="text-xs" style={{ color: '#8A9992' }}>
              Redirecionando para o login...
            </p>
            <Loader2 className="h-5 w-5 animate-spin mx-auto mt-4" style={{ color: '#4A6B5D' }} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Formulário de redefinição de senha
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: '#FDFBF7' }}
    >
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center">
          <div 
            className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
            style={{ backgroundColor: '#4A6B5D' }}
          >
            <span className="text-xl font-bold text-white">CI</span>
          </div>
          <h1 
            className="text-2xl font-bold tracking-tight"
            style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}
          >
            ClinicaInterequipe
          </h1>
        </div>

        {/* Card */}
        <Card 
          className="border shadow-sm"
          style={{ 
            backgroundColor: '#FFFFFF', 
            borderColor: '#EAE6DF',
            borderRadius: '1rem'
          }}
        >
          <CardHeader className="space-y-1 pb-4">
            <CardTitle 
              className="text-xl font-semibold"
              style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}
            >
              Redefinir Senha
            </CardTitle>
            <CardDescription style={{ color: '#5C6B64' }}>
              Digite sua nova senha
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {error && (
              <Alert 
                variant="destructive" 
                className="border"
                style={{ 
                  backgroundColor: '#FEF2F2', 
                  borderColor: '#B75D69',
                  borderRadius: '0.75rem'
                }}
              >
                <AlertDescription style={{ color: '#B75D69' }}>
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label 
                  htmlFor="password"
                  className="text-sm font-medium"
                  style={{ color: '#5C6B64' }}
                >
                  Nova Senha
                </Label>
                <div className="relative">
                  <Lock 
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                    style={{ color: '#8A9992' }}
                  />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11"
                    style={{ 
                      backgroundColor: '#FFFFFF',
                      borderColor: '#EAE6DF',
                      borderRadius: '0.75rem',
                      color: '#2C3632'
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: '#8A9992' }}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs" style={{ color: '#8A9992' }}>
                  Mínimo de 6 caracteres
                </p>
              </div>

              <div className="space-y-2">
                <Label 
                  htmlFor="confirmPassword"
                  className="text-sm font-medium"
                  style={{ color: '#5C6B64' }}
                >
                  Confirmar Nova Senha
                </Label>
                <div className="relative">
                  <Lock 
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                    style={{ color: '#8A9992' }}
                  />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 h-11"
                    style={{ 
                      backgroundColor: '#FFFFFF',
                      borderColor: '#EAE6DF',
                      borderRadius: '0.75rem',
                      color: '#2C3632'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: '#8A9992' }}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 font-medium transition-all hover:opacity-90"
                style={{ 
                  backgroundColor: '#4A6B5D',
                  color: '#FFFFFF',
                  borderRadius: '0.75rem'
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Redefinindo...
                  </>
                ) : (
                  'Redefinir Senha'
                )}
              </Button>
            </form>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm hover:underline transition-colors"
                style={{ color: '#4A6B5D' }}
              >
                Voltar ao Login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
